import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import { updateHallBookingFinancials } from '../api';
import {
  computeChargePreview,
  formatHallCurrency,
  resolveHallPaymentSummary,
} from '../utils/hallFinancials';
import {
  getHallDrawerPaperSx,
  getHallFormInputSx,
} from '../utils/hallDrawerStyles';

const HALL_ACCENT = '#4ce1b6';
const GST_OPTIONS = [0, 5, 12, 18];

export const HALL_EXTRA_QUICK_REASONS = [
  'Decoration',
  'Stage setup',
  'Sound system',
  'Projector / screen',
  'Lighting',
  'DJ / music',
  'Extra chairs',
  'Extra tables',
  'Catering support',
  'Buffet setup',
  'Cleaning charges',
  'Security staff',
  'Generator / power',
  'AC overtime',
  'Parking',
  'Valet',
  'Photography support',
  'Floral arrangement',
  'Mandap / backdrop',
  'LED wall',
  'Mic / AV technician',
  'Overtime charges',
  'Damage recovery',
  'Waiting charges',
  'Misc. service',
];

function parseReasonTokens(text) {
  return String(text || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function joinReasonTokens(tokens) {
  return tokens.join(', ');
}

function appendUniqueReason(currentNote, reason) {
  const tokens = parseReasonTokens(currentNote);
  const key = reason.trim().toLowerCase();
  if (!key || tokens.some((token) => token.toLowerCase() === key)) {
    return joinReasonTokens(tokens);
  }
  return joinReasonTokens([...tokens, reason.trim()]);
}

function isReasonSelected(note, reason) {
  const key = reason.trim().toLowerCase();
  return parseReasonTokens(note).some((token) => token.toLowerCase() === key);
}

export default function HallAddExtraChargesDrawer({
  open,
  onClose,
  bookingId,
  booking,
  payload,
  isDark,
  locked = false,
  editingExtra = null,
  onExtraAdded,
  onExtraUpdated,
}) {
  const summary = useMemo(
    () => resolveHallPaymentSummary(payload, booking),
    [payload, booking],
  );
  const inputSx = getHallFormInputSx(isDark);
  const isEditing = Boolean(editingExtra?.id);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [applyGst, setApplyGst] = useState(true);
  const [gstPercent, setGstPercent] = useState(18);
  const [customGst, setCustomGst] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editingExtra?.id) {
      const base = Number(editingExtra.base_amount) || 0;
      const percent = Number(editingExtra.gst_percent) || 0;
      const hasGst = Number(editingExtra.gst_amount) > 0 || percent > 0;
      setDescription(String(editingExtra.label || editingExtra.reason || ''));
      setAmount(base > 0 ? String(base) : '');
      setApplyGst(hasGst);
      if (hasGst && GST_OPTIONS.includes(percent)) {
        setGstPercent(percent);
        setCustomGst('');
      } else if (hasGst) {
        setGstPercent(percent);
        setCustomGst(String(percent));
      } else {
        setGstPercent(0);
        setCustomGst('');
      }
    } else {
      setDescription('');
      setAmount('');
      setApplyGst(true);
      setGstPercent(18);
      setCustomGst('');
    }
    setError('');
    setSubmitting(false);
  }, [open, editingExtra]);

  const preview = useMemo(
    () => computeChargePreview(amount, applyGst ? (customGst !== '' ? customGst : gstPercent) : 0, applyGst),
    [amount, applyGst, customGst, gstPercent],
  );

  const existingExtras = summary.financial_info.extra_charges || [];

  const handleReasonClick = (reason) => {
    setDescription((prev) => appendUniqueReason(prev, reason));
  };

  const handleSubmit = async () => {
    if (locked) {
      setError('Billing is locked for this event.');
      return;
    }
    const label = String(description || '').trim();
    if (!label) {
      setError('Description is required.');
      return;
    }
    if (preview.base_amount <= 0) {
      setError('Charge amount must be greater than zero.');
      return;
    }
    if (!bookingId) {
      setError('Booking is missing.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const mappedExisting = existingExtras.map((item) => ({
        id: item.id,
        label: item.label,
        base_amount: item.base_amount,
        gst_percent: item.gst_percent,
        gst_amount: item.gst_amount,
        total: item.total,
        created_at: item.created_at,
      }));
      const nextLine = {
        id: isEditing ? editingExtra.id : undefined,
        label,
        base_amount: preview.base_amount,
        gst_percent: preview.gst_percent,
        gst_amount: preview.gst_amount,
        total: preview.total,
        created_at: isEditing ? editingExtra.created_at : undefined,
      };
      const nextExtras = isEditing
        ? mappedExisting.map((item) => (String(item.id) === String(editingExtra.id) ? { ...item, ...nextLine } : item))
        : [...mappedExisting, nextLine];

      const result = await updateHallBookingFinancials(bookingId, {
        extra_charges: nextExtras,
        discounts: summary.financial_info.discounts || [],
      });
      if (isEditing) {
        onExtraUpdated?.(result);
      } else {
        onExtraAdded?.(result);
      }
      onClose?.();
    } catch (err) {
      setError(err?.userMessage || err?.message || (isEditing ? 'Failed to update extra charge' : 'Failed to add extra charge'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{ sx: getHallDrawerPaperSx(isDark) }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(HALL_ACCENT, isDark ? 0.22 : 0.18),
                color: isDark ? HALL_ACCENT : '#047857',
              }}
            >
              <BuildIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                {isEditing ? 'Edit Extra Charge' : 'Extra Charges'}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                Own GST per charge — not included in Event GST
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.25, py: 2, flex: 1, overflow: 'auto' }}>
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
            Quick reasons
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 2 }}>
            {HALL_EXTRA_QUICK_REASONS.map((reason) => {
              const selected = isReasonSelected(description, reason);
              return (
                <Chip
                  key={reason}
                  size="small"
                  label={reason}
                  clickable
                  disabled={submitting || locked}
                  onClick={() => handleReasonClick(reason)}
                  sx={{
                    height: 26,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    backgroundColor: selected
                      ? alpha(HALL_ACCENT, isDark ? 0.28 : 0.2)
                      : alpha(HALL_ACCENT, isDark ? 0.12 : 0.1),
                    color: isDark ? HALL_ACCENT : '#0f766e',
                    border: selected ? `1px solid ${alpha(HALL_ACCENT, 0.55)}` : '1px solid transparent',
                  }}
                />
              );
            })}
          </Stack>

          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
            {isEditing ? 'Update charge' : 'Add new charge'}
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Description"
              placeholder="e.g. Stage decoration + lighting"
              value={description}
              disabled={submitting || locked}
              onChange={(event) => setDescription(event.target.value.slice(0, 160))}
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Charge amount (excl. GST)"
              type="number"
              value={amount}
              disabled={submitting || locked}
              onChange={(event) => setAmount(event.target.value)}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />

            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${alpha(HALL_ACCENT, 0.22)}`,
                backgroundColor: isDark ? alpha('#fff', 0.03) : alpha(HALL_ACCENT, 0.04),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                  GST on this charge
                </Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={applyGst}
                      disabled={submitting || locked}
                      onChange={(event) => {
                        setApplyGst(event.target.checked);
                        if (!event.target.checked) {
                          setGstPercent(0);
                          setCustomGst('');
                        } else {
                          setGstPercent(18);
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: HALL_ACCENT },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: HALL_ACCENT },
                      }}
                    />
                  )}
                  label={applyGst ? 'Apply' : 'No GST'}
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.75rem',
                      color: isDark ? '#94a3b8' : '#64748b',
                    },
                  }}
                />
              </Stack>

              {applyGst ? (
                <Stack spacing={1.1}>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {GST_OPTIONS.map((option) => {
                      const selected = customGst === '' && Number(gstPercent) === option;
                      return (
                        <Chip
                          key={option}
                          size="small"
                          label={`${option}%`}
                          clickable
                          disabled={submitting || locked}
                          onClick={() => {
                            setGstPercent(option);
                            setCustomGst('');
                          }}
                          sx={{
                            fontWeight: 700,
                            color: selected ? '#04120e' : (isDark ? '#cbd5e1' : '#334155'),
                            bgcolor: selected
                              ? HALL_ACCENT
                              : (isDark ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1)),
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <TextField
                    label="Or enter custom %"
                    type="number"
                    value={customGst}
                    disabled={submitting || locked}
                    onChange={(event) => setCustomGst(event.target.value)}
                    fullWidth
                    size="small"
                    sx={inputSx}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    helperText="Use when the rate is not in the chips above"
                  />
                </Stack>
              ) : null}
            </Box>
          </Stack>

          <Box
            sx={{
              mt: 2,
              p: 1.35,
              borderRadius: 2,
              border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.7)}`,
              backgroundColor: isDark ? alpha('#0f172a', 0.4) : alpha('#f8fafc', 0.95),
            }}
          >
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
              Charge preview
            </Typography>
            <Stack spacing={0.4} sx={{ mt: 0.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>Base</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatHallCurrency(preview.base_amount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  GST ({preview.gst_percent}%)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatHallCurrency(preview.gst_amount)}</Typography>
              </Box>
              <Divider sx={{ my: 0.5, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>Line total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: HALL_ACCENT }}>
                  {formatHallCurrency(preview.total)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {existingExtras.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, display: 'block', mb: 0.75 }}>
                Existing extras ({existingExtras.length})
              </Typography>
              <Stack spacing={0.75}>
                {[...existingExtras].reverse().slice(0, 5).map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 1.1,
                      borderRadius: 1.5,
                      border: `1px solid ${isDark ? alpha('#64748b', 0.3) : alpha('#cbd5e1', 0.65)}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#0f172a' }} noWrap>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        {Number(item.gst_amount || 0) > 0
                          ? `GST ${item.gst_percent}%`
                          : 'No GST'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: HALL_ACCENT }}>
                      {formatHallCurrency(item.total)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mt: 1.75, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}
        </Box>

        <Box
          sx={{
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Close
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleSubmit}
            disabled={submitting || locked || preview.base_amount <= 0 || !String(description || '').trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: HALL_ACCENT,
              color: '#04120e',
              '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.92 },
            }}
          >
            {submitting ? 'Saving...' : (isEditing ? 'Save changes' : 'Add Charge')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
